import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { useToast } from '@app/core/notifications';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { socialExport, webApi } from '@mi-front/api-clients';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { socialExportPublishCardMessages } from './social-export-publish-messages';

export type SocialExportUploadCardDto =
  | webApi.CompositionSocialUploadItemDto
  | webApi.FragmentSocialUploadItemDto;

export interface UseSocialExportUploadCardActionsParams {
  upload: SocialExportUploadCardDto;
  onRetry: () => void;
  /** Runs after delete succeeds; perform all query invalidations (including getUploadQueryKey). */
  onDeleteSuccess: (uploadId: string) => Promise<void>;
  /** Runs after promote succeeds; perform all query invalidations (including getUploadQueryKey). */
  onPromoteSuccess: (uploadId: string) => Promise<void>;
}

/**
 * Overflow menu and delete dialog for a social upload card (fragment or composition list).
 * Callers supply cache invalidation and retry navigation.
 */
export function useSocialExportUploadCardActions({
  upload,
  onRetry,
  onDeleteSuccess,
  onPromoteSuccess,
}: UseSocialExportUploadCardActionsParams) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const status = upload.status;
  const platformFeatures = upload.platformFeatures;
  const platform = socialPlatforms.get(upload.platform);

  const isCompleted = status === 'COMPLETED';
  const isScheduled = status === 'SCHEDULED';
  const isFailed = status === 'FAILED';
  const hasPublicUrl = Boolean(platformFeatures?.publicUrl);
  const isWaitingPublicLink = isCompleted && !hasPublicUrl;
  const isInProgress =
    status === 'WAITING' || status === 'WAITING_CHILDREN' || status === 'IN_PROGRESS';

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async (_: socialExport.DeleteUpload200, variables: { id: string }) => {
        await onDeleteSuccess(variables.id);
        notifySuccess($t(socialExportPublishCardMessages.videoDeletedFromPlatform));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(socialExportPublishCardMessages.errorDeletingVideo));
      },
    },
  });

  const promoteMutation = socialExport.usePromoteUpload({
    mutation: {
      onSuccess: async (_: socialExport.PromoteUpload200, variables: { id: string }) => {
        await onPromoteSuccess(variables.id);
        notifySuccess($t(socialExportPublishCardMessages.publicationStarted));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(socialExportPublishCardMessages.errorPublishingNow));
      },
    },
  });

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isWaitingPublicLink || isInProgress) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isWaitingPublicLink || isInProgress) {
      setAnchorEl(null);
    }
  }, [isInProgress, isWaitingPublicLink]);

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  const handleRetry = () => {
    handleClose();
    onRetry();
  };

  const hasManageUrl =
    platformFeatures &&
    platformFeatures.manageUrl &&
    platformFeatures.manageUrl !== platformFeatures.publicUrl;

  const hasCompletedMenuItems =
    isCompleted && ((hasManageUrl && platformFeatures?.manageUrl) || platformFeatures?.isDeletable);
  const hasScheduledMenuItems = isScheduled;
  const hasFailedMenuItems = isFailed;

  const hasAnyMenuItems = hasCompletedMenuItems || hasScheduledMenuItems || hasFailedMenuItems;

  const trigger =
    isWaitingPublicLink || isInProgress ? null : (
      <Button size="small" variant="outlined" onClick={handleOpen} sx={{ minWidth: 0, px: 1 }}>
        <MoreHorizIcon fontSize="small" />
      </Button>
    );

  const menuAndDialog =
    isWaitingPublicLink || isInProgress ? null : (
      <>
        <Menu
          anchorEl={anchorEl}
          open={open && !isWaitingPublicLink}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {!hasAnyMenuItems ? (
            <MenuItem disabled sx={{ cursor: 'default' }}>
              <ListItemText>
                <FormattedMessage {...socialExportPublishCardMessages.noAdditionalActions} />
              </ListItemText>
            </MenuItem>
          ) : (
            <>
              {isCompleted && hasManageUrl && platformFeatures?.manageUrl && (
                <MenuItem
                  component="a"
                  href={platformFeatures.manageUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleClose}
                >
                  <ListItemIcon>
                    <BuildIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    <FormattedMessage
                      {...socialExportPublishCardMessages.manageVideo}
                      values={{ platform: platform.name }}
                    />
                  </ListItemText>
                </MenuItem>
              )}
              {isCompleted && platformFeatures?.isDeletable && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>
                    <FormattedMessage
                      {...socialExportPublishCardMessages.deleteFromPlatform}
                      values={{ platform: platform.name }}
                    />
                  </ListItemText>
                </MenuItem>
              )}
              {isScheduled && (
                <>
                  <ConfirmationAction
                    title={$t(socialExportPublishCardMessages.publishNowConfirmTitle)}
                    message={
                      <FormattedMessage
                        {...socialExportPublishCardMessages.publishNowConfirmMessage}
                      />
                    }
                    confirmLabel={$t(socialExportPublishCardMessages.publishNow)}
                    cancelLabel={$t({
                      id: 'common.cancel',
                      defaultMessage: 'Cancelar',
                    })}
                    confirmColor="primary"
                    onConfirm={handlePromote}
                  >
                    {({ onClick }) => (
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          onClick();
                        }}
                        disabled={promoteMutation.isPending}
                      >
                        <ListItemIcon>
                          <SendIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          <FormattedMessage {...socialExportPublishCardMessages.publishNow} />
                        </ListItemText>
                      </MenuItem>
                    )}
                  </ConfirmationAction>
                  <ConfirmationAction
                    title={$t(socialExportPublishCardMessages.deleteScheduledPublishTitle)}
                    message={
                      <FormattedMessage
                        {...socialExportPublishCardMessages.deleteScheduledPublishMessage}
                      />
                    }
                    confirmLabel={$t({
                      id: 'common.delete',
                      defaultMessage: 'Eliminar',
                    })}
                    cancelLabel={$t({
                      id: 'common.cancel',
                      defaultMessage: 'Cancelar',
                    })}
                    confirmColor="error"
                    onConfirm={handleDelete}
                  >
                    {({ onClick }) => (
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          onClick();
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <ListItemIcon>
                          <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
                        </ListItemText>
                      </MenuItem>
                    )}
                  </ConfirmationAction>
                </>
              )}
              {isFailed && (
                <MenuItem onClick={handleRetry}>
                  <ListItemIcon>
                    <SendIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    <FormattedMessage {...socialExportPublishCardMessages.retry} />
                  </ListItemText>
                </MenuItem>
              )}
            </>
          )}
        </Menu>
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {$t(socialExportPublishCardMessages.deleteVideoFromPlatformTitle, {
              platform: platform.name,
            })}
          </DialogTitle>
          <DialogContent>
            <FormattedMessage {...socialExportPublishCardMessages.deleteVideoFromPlatformMessage} />
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              {$t({
                id: 'common.cancel',
                defaultMessage: 'Cancelar',
              })}
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await handleDelete();
                setDeleteDialogOpen(false);
              }}
            >
              {$t({
                id: 'common.delete',
                defaultMessage: 'Eliminar',
              })}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );

  return { trigger, menuAndDialog };
}
